import * as pc from 'playcanvas'
import { spawnStationProps } from './dressing'

export const STATION_STATUS_COLORS: Record<string, [number, number, number]> = {
  completed: [0.133, 0.827, 0.933],
  running: [1, 0.65, 0.15],
  queued: [0.58, 0.639, 0.722],
  failed: [0.973, 0.443, 0.443],
  cancelled: [0.42, 0.447, 0.502],
}

const DEFAULT_COLOR = STATION_STATUS_COLORS.queued
const STATION_PAD_BASE_COLOR: [number, number, number] = [0.18, 0.16, 0.14]
const STATION_PAD_STATUS_MIX = 0.18

/**
 * Canonical production-pod proportions in metres. The authored slot grid stays
 * untouched; these inset surfaces leave visible floor between neighboring
 * stations instead of tiling four status-colored slabs into one harsh rug.
 */
export const STATION_VISUAL_GEOMETRY = {
  pad: {
    position: [0, 0.014, 0] as [number, number, number],
    scale: [1.55, 0.028, 1.3] as [number, number, number],
  },
  divider: {
    position: [0, 0.72, -0.78] as [number, number, number],
    scale: [1.45, 0.88, 0.04] as [number, number, number],
  },
  lamp: {
    position: [-0.62, 0.86, -0.52] as [number, number, number],
    scale: [0.14, 0.14, 0.14] as [number, number, number],
  },
  monitor: {
    position: [0, 0.98, -0.5] as [number, number, number],
    scale: [0.42, 0.24, 0.018] as [number, number, number],
  },
} as const

export interface StationVisualMaterials {
  pad: pc.StandardMaterial
  divider: pc.StandardMaterial
  lamp: pc.StandardMaterial
  monitor: pc.StandardMaterial
}

export interface StationVisual {
  padMeshInstances: pc.MeshInstance[]
  dividerMeshInstances: pc.MeshInstance[]
  lampMeshInstances: pc.MeshInstance[]
  monitorMeshInstances: pc.MeshInstance[]
}

function colorFor(status: string): [number, number, number] {
  return STATION_STATUS_COLORS[status] ?? DEFAULT_COLOR
}

/** Muted textile color: status remains legible without turning the rug neon. */
export function stationPadColor(status: string): [number, number, number] {
  const statusColor = colorFor(status)
  return STATION_PAD_BASE_COLOR.map((channel, index) =>
    channel + (statusColor[index] - channel) * STATION_PAD_STATUS_MIX,
  ) as [number, number, number]
}

/** Create the one material vocabulary shared by roster and live-run desks. */
export function createStationVisualMaterials(
  status: string,
  attachRunningMonitor?: (material: pc.StandardMaterial) => void,
): StationVisualMaterials {
  const pad = new pc.StandardMaterial()
  pad.useMetalness = true
  pad.metalness = 0
  pad.gloss = 0.12

  const divider = new pc.StandardMaterial()
  divider.diffuse = new pc.Color(0.3, 0.33, 0.4)
  divider.gloss = 0.15

  const lamp = new pc.StandardMaterial()

  const monitor = new pc.StandardMaterial()
  monitor.diffuse = new pc.Color(0.03, 0.04, 0.05)
  monitor.emissiveIntensity = 0.9

  const materials = { pad, divider, lamp, monitor }
  applyStationVisualStatus(materials, status)
  if (status === 'running') attachRunningMonitor?.(monitor)
  return materials
}

/** Update a station's complete status palette without rebuilding its geometry. */
export function applyStationVisualStatus(
  materials: StationVisualMaterials,
  status: string,
): void {
  applyStationSurfaceStatus(
    materials.pad,
    materials.divider,
    materials.lamp,
    status,
  )
  const [r, g, b] = colorFor(status)
  materials.monitor.emissive = new pc.Color(r * 0.7, g * 0.7, b * 0.7)
  materials.monitor.update()
}

/** Shared three-surface status formula retained by the legacy public helper. */
export function applyStationSurfaceStatus(
  pad: pc.StandardMaterial,
  divider: pc.StandardMaterial,
  lamp: pc.StandardMaterial,
  status: string,
): void {
  const [r, g, b] = colorFor(status)
  pad.diffuse = new pc.Color(...stationPadColor(status))
  pad.update()
  divider.emissive = new pc.Color(r * 0.18, g * 0.18, b * 0.18)
  divider.update()
  lamp.diffuse = new pc.Color(r, g, b)
  lamp.emissive = new pc.Color(r * 0.9, g * 0.9, b * 0.9)
  lamp.update()
}

function box(
  parent: pc.Entity,
  name: string,
  position: [number, number, number],
  scale: [number, number, number],
  material: pc.StandardMaterial,
  batchGroupId?: number,
): pc.MeshInstance[] {
  const entity = new pc.Entity(name)
  entity.addComponent('render', { type: 'box' })
  entity.setLocalScale(...scale)
  entity.setLocalPosition(...position)
  const instances = entity.render ? [...entity.render.meshInstances] : []
  for (const instance of instances) instance.material = material
  if (entity.render) {
    // Status pads, dividers, lamps, and monitor quads are tiny repeated
    // surfaces. Their shape is readable through lighting without paying a
    // cascaded shadow submission for every one of 20 stations.
    entity.render.castShadows = false
    if (batchGroupId !== undefined) entity.render.batchGroupId = batchGroupId
  }
  parent.addChild(entity)
  return instances
}

/**
 * Build the canonical HQ desk pod. Both projected roster agents and unbound
 * live runs call this exact helper, so the desk, chair, divider, status lamp,
 * and monitor geometry cannot drift into two visual dialects.
 */
export function spawnStationVisual(
  app: pc.Application,
  root: pc.Entity,
  materials: StationVisualMaterials,
  options: { batchGroupId?: number } = {},
): StationVisual {
  const batch = options.batchGroupId
  const padMeshInstances = box(
    root,
    'pad',
    [...STATION_VISUAL_GEOMETRY.pad.position],
    [...STATION_VISUAL_GEOMETRY.pad.scale],
    materials.pad,
    batch,
  )
  const dividerMeshInstances = box(
    root,
    'screen',
    [...STATION_VISUAL_GEOMETRY.divider.position],
    [...STATION_VISUAL_GEOMETRY.divider.scale],
    materials.divider,
    batch,
  )
  const lampMeshInstances = box(
    root,
    'lamp',
    [...STATION_VISUAL_GEOMETRY.lamp.position],
    [...STATION_VISUAL_GEOMETRY.lamp.scale],
    materials.lamp,
    batch,
  )
  const monitorMeshInstances = box(
    root,
    'monitorGlow',
    [...STATION_VISUAL_GEOMETRY.monitor.position],
    [...STATION_VISUAL_GEOMETRY.monitor.scale],
    materials.monitor,
    batch,
  )
  spawnStationProps(app, root, { batchGroupId: batch })
  return {
    padMeshInstances,
    dividerMeshInstances,
    lampMeshInstances,
    monitorMeshInstances,
  }
}

/** Rebind an existing canonical pod to a shared status material set. */
export function bindStationVisualMaterials(
  visual: StationVisual,
  materials: StationVisualMaterials,
): void {
  for (const instance of visual.padMeshInstances) instance.material = materials.pad
  for (const instance of visual.dividerMeshInstances) instance.material = materials.divider
  for (const instance of visual.lampMeshInstances) instance.material = materials.lamp
  for (const instance of visual.monitorMeshInstances) instance.material = materials.monitor
}
